/**
 * RivalryHub (/r/:slug) — Sports Mode v0 landing page.
 *
 * iter-85 ships the static-curated MVP per the user's spec:
 *   1. First-time visitor: side picker (TeamA · TeamB · Neutral) →
 *      mood picker (Petty · Proud · Funny · Pain · Respectful) →
 *      curated 6-card deck.
 *   2. Returning visitor (side+mood persisted in localStorage): skip
 *      setup and show the deck straight away. A small "switch side" /
 *      "change mood" pill restores the pickers.
 *   3. Live-event banner — polls /events every 15s when game.status ===
 *      "live". When a new event lands, the banner pulses for 3s with
 *      the headline + auto-rotates the deck to the matching moment_type.
 *
 * Design posture: NOT a sports app. The card is still the product. The
 * hub just funnels the right card to the right side at the right moment.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { ArrowRight, RotateCcw, Share2, Bookmark, Zap } from "lucide-react";
import { toast } from "sonner";
import MobileTopBar from "../components/mobile/MobileTopBar";
import HamburgerSheet from "../components/mobile/HamburgerSheet";
import { Toaster } from "../components/ui/sonner";
import { getSportsGame, getSportsCards, getGameEvents } from "../lib/sportsApi";
import { canonicalShareUrl } from "../lib/shareUrl";
import { shareCardNative } from "../lib/webShare";
import axios from "axios";
import { API } from "../lib/api";
import SportsGateSheet from "../components/SportsGateSheet";

const MOODS = [
    { key: "petty", label: "petty", emoji: "🫠" },
    { key: "proud", label: "proud", emoji: "🦁" },
    { key: "funny", label: "funny", emoji: "💀" },
    { key: "pain", label: "pain", emoji: "🥲" },
    { key: "respectful", label: "respect", emoji: "🙏" },
];

const lsKey = (slug) => `moodcard.sports.${slug}`;

function loadPersisted(slug) {
    try {
        const raw = window.localStorage.getItem(lsKey(slug));
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function savePersisted(slug, obj) {
    try {
        window.localStorage.setItem(lsKey(slug), JSON.stringify(obj));
    } catch { /* ignore */ }
}

function teamPill({ team, active, onClick }) {
    if (!team) return null;
    return (
        <button
            key={team.id}
            type="button"
            onClick={onClick}
            data-testid={`rivalry-side-${team.id}`}
            aria-pressed={active}
            className={`flex-1 px-4 py-4 rounded-2xl border transition-all text-left ${
                active
                    ? "border-amber-300/60 bg-amber-400/8 ring-1 ring-amber-300/40"
                    : "border-white/12 hover:border-white/30 bg-black/25"
            }`}
        >
            <div
                className="font-display text-base tracking-[0.18em] uppercase"
                style={{ color: team.primary_color || "#fff" }}
            >
                {team.short || team.id}
            </div>
            <div className="font-serif italic text-[12px] text-app-2 mt-0.5">
                {team.full}
            </div>
        </button>
    );
}

function SportsCard({ q, game, side, onRemix, onShare, onKeep, kept }) {
    // Pick team accent color by which side the card is for.
    const team = side === "neutral"
        ? null
        : game?.away_team?.id === side
            ? game.away_team
            : game?.home_team;
    const accent = team?.primary_color || "#E8C770";
    const ink = team?.id === "spurs" ? "#0a0a0a" : "#0a0a0a";
    // iter-87: prefer the curated background image (if attached) over the
    // gradient. Backend resolves /api/static/... relative; we prepend
    // REACT_APP_BACKEND_URL to make it work against any deploy.
    const bgUrl = q.background_url
        ? `${process.env.REACT_APP_BACKEND_URL}${q.background_url}`
        : null;
    const hasBg = !!bgUrl;
    // iter-87b · pick scrim preset based on the per-image luminance signal
    // that the backend computed at upload time:
    //   "dark"  → heavier overlay (image is bright; protect white text)
    //   "light" → lighter overlay (image is already dark; let it breathe)
    const scrimKind = q.background_scrim || "dark";
    const scrimGradient = scrimKind === "light"
        ? "linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.10) 38%, rgba(0,0,0,0.62) 100%)"
        : "linear-gradient(180deg, rgba(0,0,0,0.48) 0%, rgba(0,0,0,0.22) 35%, rgba(0,0,0,0.82) 100%)";
    const textShadow = scrimKind === "light"
        ? "0 1px 8px rgba(0,0,0,0.55)"
        : "0 2px 14px rgba(0,0,0,0.70)";
    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            data-testid={`sports-card-${q.copy?.slice(0, 16) || "x"}`}
            className="relative rounded-3xl overflow-hidden border border-white/8 shadow-lg"
            style={{
                background: hasBg
                    ? "#0a0a0a"
                    : `linear-gradient(180deg, ${accent} 0%, ${team?.accent_color || "#0a0a0a"} 100%)`,
                color: hasBg ? "#ffffff" : ink,
                minHeight: 260,
            }}
        >
            {hasBg && (
                <>
                    <img
                        src={bgUrl}
                        alt=""
                        aria-hidden="true"
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover"
                        style={{ opacity: 0.86 }}
                    />
                    {/* iter-87b · per-image scrim preset */}
                    <div
                        className="absolute inset-0"
                        aria-hidden="true"
                        style={{ background: scrimGradient }}
                    />
                </>
            )}
            <div className={`relative z-[1] absolute top-3 left-4 right-4 flex items-center justify-between text-[10px] tracking-[0.22em] uppercase font-mono`}
                 style={{ color: hasBg ? "#ffffff" : ink, opacity: hasBg ? 0.9 : 0.7 }}>
                <span>{team?.short || "neutral"}</span>
                <span>{q.moment_type}</span>
            </div>
            <div className="relative z-[1] px-6 pt-12 pb-16 flex items-center justify-center" style={{ minHeight: 260 }}>
                <p
                    className="font-display uppercase text-2xl leading-tight text-center"
                    style={{
                        color: hasBg ? "#ffffff" : ink,
                        fontWeight: 700,
                        letterSpacing: "-0.01em",
                        textShadow: hasBg ? textShadow : "none",
                    }}
                >
                    {q.copy}
                </p>
            </div>
            <div className="relative z-[1] absolute bottom-2 left-4 right-4 flex items-center justify-between font-mono text-[9px] tracking-[0.22em] uppercase"
                 style={{ color: hasBg ? "#ffffff" : ink, opacity: hasBg ? 0.85 : 0.62 }}>
                <span>made with moodcard.app</span>
                {q.player && <span>· {q.player}</span>}
            </div>
            {/* Action bar overlayed at the bottom edge */}
            <div className="relative z-[2] absolute bottom-0 inset-x-0 flex items-stretch gap-px bg-black/65 backdrop-blur-md border-t border-white/8">
                <button
                    type="button"
                    onClick={onShare}
                    data-testid="sports-card-share"
                    className="flex-1 py-3 flex items-center justify-center gap-2 text-white/95 font-sans text-[11px] tracking-[0.18em] uppercase hover:bg-white/5 transition-colors"
                >
                    <Share2 className="w-3.5 h-3.5" strokeWidth={1.8} />
                    share
                </button>
                <button
                    type="button"
                    onClick={onRemix}
                    data-testid="sports-card-remix"
                    className="flex-1 py-3 flex items-center justify-center gap-2 text-white/95 font-sans text-[11px] tracking-[0.18em] uppercase hover:bg-white/5 transition-colors"
                >
                    <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.8} />
                    remix
                </button>
                <button
                    type="button"
                    onClick={onKeep}
                    data-testid="sports-card-keep"
                    className={`flex-1 py-3 flex items-center justify-center gap-2 font-sans text-[11px] tracking-[0.18em] uppercase hover:bg-white/5 transition-colors ${kept ? "text-amber-200" : "text-white/95"}`}
                >
                    <Bookmark className="w-3.5 h-3.5" strokeWidth={1.8} fill={kept ? "rgb(251, 191, 36)" : "none"} />
                    {kept ? "kept" : "keep"}
                </button>
            </div>
        </motion.div>
    );
}

export default function RivalryHub() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [game, setGame] = useState(null);
    const [loadErr, setLoadErr] = useState(false);
    const [side, setSide] = useState(null);
    const [mood, setMood] = useState(null);
    const [cards, setCards] = useState([]);
    const [keptIds, setKeptIds] = useState({});
    const [hamburgerOpen, setHamburgerOpen] = useState(false);
    const [latestEvent, setLatestEvent] = useState(null);
    const [eventsLastSeen, setEventsLastSeen] = useState(null);
    const [activeMoment, setActiveMoment] = useState(null); // optional moment_type override
    // iter-96 — share gate state. When share-event endpoint returns
    // ok=false, we open the SportsGateSheet with game/series pass options.
    const [gateOpen, setGateOpen] = useState(false);
    const [gate, setGate] = useState(null);
    // iter-97 — running share-count for the soft warning ("8/10 tonight").
    const [shareStatus, setShareStatus] = useState(null);

    // Hydrate game + persisted prefs on mount.
    useEffect(() => {
        let cancelled = false;
        getSportsGame(slug)
            .then((g) => {
                if (cancelled) return;
                setGame(g);
                const persisted = loadPersisted(slug);
                if (persisted) {
                    setSide(persisted.side);
                    setMood(persisted.mood);
                }
            })
            .catch(() => { if (!cancelled) setLoadErr(true); });
        return () => { cancelled = true; };
    }, [slug]);

    // Fetch deck when (side, mood, activeMoment) changes.
    useEffect(() => {
        if (!side || !mood) return;
        let cancelled = false;
        getSportsCards(slug, {
            side,
            tone: mood,
            moment_type: activeMoment || undefined,
            limit: 12,
        })
            .then((items) => {
                if (cancelled) return;
                setCards(items);
            })
            .catch(() => { if (!cancelled) setCards([]); });
        return () => { cancelled = true; };
    }, [slug, side, mood, activeMoment]);

    // Live-event polling — only when game.status === "live". Polls every 15s
    // (8s when the user is in the tab and recently active; we keep it simple
    // for tonight and use a fixed 15s interval).
    useEffect(() => {
        if (!game || game.status !== "live") return undefined;
        let cancelled = false;
        let timer = null;

        const poll = async () => {
            try {
                const items = await getGameEvents(slug, eventsLastSeen);
                if (cancelled || !items.length) return;
                // Most recent first from the API; the freshest is items[0]
                const latest = items[0];
                setLatestEvent(latest);
                setEventsLastSeen(latest.created_at || new Date().toISOString());
                // Auto-rotate to the matching moment_type. Cleared after 18s
                // so the user returns to their chosen mood deck.
                if (latest.moment_type) {
                    setActiveMoment(latest.moment_type);
                    setTimeout(() => setActiveMoment(null), 18000);
                }
            } catch { /* ignore */ }
        };

        // Kick once immediately, then every 15s.
        poll();
        timer = setInterval(poll, 15000);
        return () => { cancelled = true; if (timer) clearInterval(timer); };
    }, [game, slug, eventsLastSeen]);

    // iter-97 — fetch share-status on mount + after each share so the
    // "8/10 tonight" warning stays accurate without polling.
    useEffect(() => {
        let cancelled = false;
        axios.get(`${API}/sports/${slug}/share-status`, { withCredentials: true })
            .then(({ data }) => { if (!cancelled) setShareStatus(data); })
            .catch(() => { /* non-fatal */ });
        return () => { cancelled = true; };
    }, [slug]);

    const pickSide = useCallback((s) => {
        setSide(s);
        if (mood) savePersisted(slug, { side: s, mood });
    }, [slug, mood]);

    const pickMood = useCallback((m) => {
        setMood(m);
        if (side) savePersisted(slug, { side, mood: m });
    }, [slug, side]);

    const resetPickers = () => {
        setSide(null);
        setMood(null);
        try { window.localStorage.removeItem(lsKey(slug)); } catch { /* ignore */ }
    };

    const handleRemix = (q) => {
        // Remix → take the line into the standard create flow as the starting feeling.
        try {
            window.localStorage.setItem("moodcard.remix_seed", q.copy);
        } catch { /* ignore */ }
        navigate("/?feeling=" + encodeURIComponent(q.copy));
    };

    const handleShare = async (q) => {
        const shareTitle = q.title_hashtag ? `#${q.title_hashtag}` : "MoodCard · " + (game?.label || slug);
        const url = canonicalShareUrl(`/r/${slug}`);
        const text = `${q.copy}\n— ${game?.label || ""}`;

        // iter-95/96 — pre-flight the share gate. Record-event endpoint
        // returns `ok:false` with `headline/advice` + pricing once the
        // anon actor (or signed-in user without a pass) has shared this
        // game 10 times. We surface the dedicated /pass/{slug} purchase
        // sheet so the user can convert in one tap.
        try {
            const { data: gate } = await axios.post(
                `${API}/sports/${slug}/share-event`,
                { slug, side: q.side },
                { withCredentials: true },
            );
            if (gate && gate.ok === false) {
                // Open the pass purchase modal regardless of login state —
                // the modal itself prompts sign-in when needed.
                setGate(gate);
                setGateOpen(true);
                return;
            }
            // iter-97 — refresh share-status after a successful share so the
            // warning pill counts down in real-time.
            if (gate && gate.ok === true) {
                setShareStatus({
                    shared_count: gate.shared_count,
                    is_signed_in: gate.is_signed_in,
                    has_event_pass: gate.has_event_pass,
                    pass_type: gate.pass_type,
                    limit: gate.has_event_pass ? null : 10,
                    remaining_free: gate.remaining_free,
                });
            }
        } catch (e) {
            // Non-fatal — if the gate endpoint failed, let the share proceed
            // anyway so we never block on a transient backend hiccup.
            console.warn("share-gate preflight failed", e);
        }

        const ok = await shareCardNative({
            title: shareTitle,
            text,
            url,
            // No PNG file yet for sports cards (they're not /api/moodcards-backed);
            // OG meta on the URL handles the unfurl.
        });
        if (!ok) {
            try {
                await navigator.clipboard.writeText(`${text}\n${url}`);
                toast.success("copied. paste it anywhere.");
            } catch {
                toast.error("share failed — long-press to copy");
            }
        }
    };

    const handleKeep = (q) => {
        const id = `${q.side}|${q.moment_type}|${q.copy}`;
        setKeptIds((m) => ({ ...m, [id]: !m[id] }));
        toast.success(keptIds[id] ? "removed" : "kept for later");
    };

    const isLive = game?.status === "live";
    const setupComplete = side && mood;

    const teamA = game?.away_team;
    const teamB = game?.home_team;

    const pageTitle = game?.label || "rivalry hub";

    if (loadErr) {
        return (
            <div className="min-h-screen bg-app text-app flex items-center justify-center px-6 text-center">
                <div>
                    <p className="font-serif italic text-xl text-app-2">that game isn't on yet.</p>
                    <button
                        onClick={() => navigate("/discover")}
                        className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-400 text-black text-xs tracking-wide font-medium"
                    >
                        explore discover
                        <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-app text-app pb-24">
            <Helmet>
                <title>{`${pageTitle} — MoodCard`}</title>
                <meta name="description" content={`Live reaction cards for ${pageTitle}. Pick a side. Post the perfect take.`} />
                <meta property="og:title" content={`${pageTitle} — MoodCard`} />
                <meta property="og:description" content="Post your reaction. Side picker + mood. Live moments." />
            </Helmet>
            <Toaster theme="dark" position="bottom-center" />
            <SportsGateSheet open={gateOpen} onClose={() => setGateOpen(false)} gate={gate} />
            <MobileTopBar
                onOpenMenu={() => setHamburgerOpen(true)}
                rightMode="create"
            />
            <HamburgerSheet open={hamburgerOpen} onClose={() => setHamburgerOpen(false)} />

            <div className="max-w-md mx-auto px-5 pt-4">
                {/* Game header */}
                <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
                    <div className="flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase text-app-3 mb-2">
                        {isLive ? (
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-rose-500/15 border border-rose-400/30 text-rose-200">
                                <span className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse" />
                                live now
                            </span>
                        ) : (
                            <span>rivalry hub</span>
                        )}
                    </div>
                    <h1 className="font-display text-3xl text-app leading-tight" data-testid="rivalry-title">
                        {teamA?.short || "team a"}
                        <span className="px-2 text-app-3 italic font-serif">vs</span>
                        {teamB?.short || "team b"}
                    </h1>
                    <p className="mt-2 font-serif italic text-sm text-app-2">
                        post the perfect reaction.
                    </p>
                </motion.div>

                {/* Live banner — appears when latestEvent fires */}
                <AnimatePresence>
                    {isLive && latestEvent && (
                        <motion.div
                            key={latestEvent.created_at}
                            initial={{ opacity: 0, scale: 0.97 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0 }}
                            className="mt-4 px-4 py-3 rounded-2xl bg-amber-400/12 border border-amber-300/35 flex items-center gap-3"
                            data-testid="rivalry-live-banner"
                        >
                            <Zap className="w-4 h-4 text-amber-200" strokeWidth={2} />
                            <span className="flex-1 font-sans text-xs tracking-wide text-app">
                                <span className="text-amber-200 uppercase tracking-[0.22em] text-[10px] mr-1">just happened</span>
                                {latestEvent.headline || latestEvent.moment_type}
                            </span>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* iter-97 — soft share-count warning. Surfaces from share #8
                    so the gate at #10 doesn't feel like an ambush paywall.
                    Hides for users with a pass and signed-in users (who are
                    not gated). */}
                {shareStatus && !shareStatus.has_event_pass && shareStatus.remaining_free !== null && shareStatus.remaining_free <= 2 && shareStatus.shared_count > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="mt-4"
                        data-testid="share-count-warning"
                    >
                        <button
                            onClick={() => {
                                setGate({
                                    game_pass_price_usd: 4.99,
                                    series_pass_price_usd: 14.0,
                                    game_slug: slug,
                                    series_slug: game?.series_slug,
                                    requires_login: !shareStatus.is_signed_in,
                                });
                                setGateOpen(true);
                            }}
                            className={[
                                "w-full inline-flex items-center justify-between gap-3 px-3.5 py-2 rounded-full border transition-all",
                                shareStatus.remaining_free === 0
                                    ? "bg-rose-500/12 border-rose-400/45 text-rose-100"
                                    : shareStatus.remaining_free === 1
                                        ? "bg-rose-500/8 border-rose-400/35 text-rose-200"
                                        : "bg-amber-300/8 border-amber-300/30 text-amber-100",
                            ].join(" ")}
                        >
                            <div className="flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                                <span className="eyebrow-wide font-semibold">
                                    {shareStatus.remaining_free === 0
                                        ? "shares done · grab a pass"
                                        : `${shareStatus.shared_count}/10 shares tonight`}
                                </span>
                            </div>
                            <span className="font-serif italic text-[11px] opacity-85">
                                {shareStatus.remaining_free === 0
                                    ? "from $4.99 →"
                                    : `${shareStatus.remaining_free} left →`}
                            </span>
                        </button>
                    </motion.div>
                )}

                {/* Setup pickers — shown until side+mood are chosen */}
                {!setupComplete && (
                    <>
                        <div className="mt-7">
                            <div className="text-[10px] tracking-[0.32em] uppercase text-app-3 mb-3">
                                who are you riding with tonight?
                            </div>
                            <div className="flex gap-2.5">
                                {teamPill({ team: teamA, active: side === teamA?.id, onClick: () => pickSide(teamA?.id) })}
                                {teamPill({ team: teamB, active: side === teamB?.id, onClick: () => pickSide(teamB?.id) })}
                            </div>
                            <button
                                type="button"
                                onClick={() => pickSide("neutral")}
                                data-testid="rivalry-side-neutral"
                                className={`mt-2 w-full px-4 py-3 rounded-2xl border font-serif italic text-sm transition-all ${
                                    side === "neutral"
                                        ? "border-amber-300/55 bg-amber-400/8 text-app"
                                        : "border-white/10 bg-black/20 text-app-2 hover:text-app"
                                }`}
                            >
                                neutral chaos — i just want the highlights
                            </button>
                        </div>

                        {side && (
                            <div className="mt-6">
                                <div className="text-[10px] tracking-[0.32em] uppercase text-app-3 mb-3">
                                    choose your fan mood
                                </div>
                                <div className="flex flex-wrap gap-2" data-testid="rivalry-mood-picker">
                                    {MOODS.map((m) => {
                                        const active = mood === m.key;
                                        return (
                                            <button
                                                key={m.key}
                                                type="button"
                                                onClick={() => pickMood(m.key)}
                                                data-testid={`rivalry-mood-${m.key}`}
                                                aria-pressed={active}
                                                className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[11px] tracking-[0.18em] uppercase font-sans transition-all ${
                                                    active
                                                        ? "bg-amber-400 text-black border border-amber-300"
                                                        : "bg-black/30 text-app-2 border border-white/12 hover:border-amber-300/40 hover:text-app"
                                                }`}
                                            >
                                                <span aria-hidden="true">{m.emoji}</span>
                                                {m.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* Deck — shown when setup is complete */}
                {setupComplete && (
                    <>
                        <div className="mt-6 flex items-center justify-between">
                            <div className="text-[10px] tracking-[0.32em] uppercase text-app-3">
                                {side === "neutral" ? "neutral" : (side === teamA?.id ? teamA?.short : teamB?.short)}
                                <span className="text-app-3/60"> · </span>
                                {mood}
                                {activeMoment && (
                                    <span className="ml-2 text-amber-200">· {activeMoment} 🔥</span>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={resetPickers}
                                className="text-[10px] tracking-[0.22em] uppercase text-app-3 hover:text-app transition-colors"
                                data-testid="rivalry-reset"
                            >
                                switch
                            </button>
                        </div>

                        <div className="mt-3 grid grid-cols-1 gap-4" data-testid="rivalry-deck">
                            {cards.length === 0 ? (
                                <p className="font-serif italic text-app-2 text-center py-12">
                                    no cards yet for this combo. try another mood?
                                </p>
                            ) : (
                                cards.map((q, i) => {
                                    const id = `${q.side}|${q.moment_type}|${q.copy}`;
                                    return (
                                        <SportsCard
                                            key={id + i}
                                            q={q}
                                            game={game}
                                            side={q.side}
                                            kept={!!keptIds[id]}
                                            onRemix={() => handleRemix(q)}
                                            onShare={() => handleShare(q)}
                                            onKeep={() => handleKeep(q)}
                                        />
                                    );
                                })
                            )}
                        </div>

                        <p className="mt-7 text-center font-serif italic text-[11px] text-app-3">
                            tap share. paste the energy.
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}
